import React, { useState, useEffect, useRef } from "react";

import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';

import ViewerStyle from '../css/Viewer.module.css'

import { API_DEFAULT } from "../services/settings";
import { API } from "../services/routes";
import { request, tokenExpired } from "../services/http";

// The Power BI report of the viewer on screen: the embed token the back
// issues for it (/pbi-auth/pbi-embed-token), the renewal of that token before
// it expires, and the embed itself. Split out of Visor.jsx on 2026-09-21 with
// no change in behaviour; the effects keep the dependencies they had there
// (the catalogue and the viewer), so the report is asked for at the same
// moments.
function ViewerEmbed(props) {

  const { viewer, catalogue, wide, accessToken, setAccessToken, getAccessTokenWithRefresh, setShowLoader, setShowAlert, setMessage, setSeverity, onEmbedded } = props;

  const [pbiEmbed, setPbiEmbed] = useState({})

  // A Power BI embed token lasts about an hour, and the back sends when it
  // expires as tokenExpiry. Without a new one the report stops answering and
  // the user has to reload, losing their filters. Replacing only the token
  // keeps them: powerbi-client-react calls setAccessToken when the token
  // changes and the embed URL does not.
  const EMBED_TOKEN_MARGIN_MS = 5 * 60 * 1000;
  const renewingEmbedToken = useRef(false);
  // When the token on screen expires, read by the checks below. It is a ref,
  // not a dependency: an effect that ran again on every new token asked for
  // one a second, for hours, when a token arrived already inside the margin,
  // and took the master user's embed quota to 100 % for every client
  // (back-ratings' handoff of 2026-09-23).
  const embedTokenExpiry = useRef(0);
  // A renewal that does not win life is not tried again until the next check:
  // the back cannot give more right now.
  const renewalWorthTrying = useRef(true);

  const renewEmbedToken = async (access = accessToken) => {
    if (renewingEmbedToken.current || !viewer) return;
    renewingEmbedToken.current = true;
    try {
      const res = await fetch(
        `${API_DEFAULT}/${API.embedToken}?workspace-id=${viewer.workspace_id}&report-id=${viewer.report_id}`, {
        headers: {
          "Authorization": "Bearer " + access
        }
      }
      )
      const data = await res.json();
      if (data['code'] === 'token_not_valid') {
        let newAccess = await getAccessTokenWithRefresh();
        if (newAccess) {
          setAccessToken(newAccess);
          localStorage.setItem('accessToken', newAccess);
          renewingEmbedToken.current = false;
          return renewEmbedToken(newAccess);
        }
      }
      else if (res.ok) {
        const renewedExpiry = new Date(data['tokenExpiry']).getTime();
        renewalWorthTrying.current = renewedExpiry > embedTokenExpiry.current;
        // Same report and embed URL, so only the token changes.
        setPbiEmbed(data)
      }
    } catch (e) {
      // A failed renewal is retried on the next check, before the token ends.
      console.error(e);
    }
    renewingEmbedToken.current = false;
  }

  // The expiry of the token on screen, kept where the checks read it without
  // making them run again.
  useEffect(() => {
    if (!pbiEmbed['tokenExpiry']) return;
    embedTokenExpiry.current = new Date(pbiEmbed['tokenExpiry']).getTime();
  }, [pbiEmbed]);

  // Checked every minute rather than with one timer per token, so a failed
  // renewal is tried again while there is still margin. One check a minute is
  // also the ceiling: a renewal never leads straight into the next check.
  useEffect(() => {
    if (!viewer) return;
    renewalWorthTrying.current = true;

    const renewIfClose = () => {
      if (!embedTokenExpiry.current || !renewalWorthTrying.current) return;
      const expiresIn = embedTokenExpiry.current - Date.now();
      if (expiresIn <= EMBED_TOKEN_MARGIN_MS) {
        renewEmbedToken();
      }
    };

    const interval = setInterval(renewIfClose, 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  // The embed configuration of the viewer on screen.
  const fetchPBIEmbedService = async (access = accessToken, afterRefresh = false) => {
    setShowLoader(true)
    // The effect below also runs on mount, before any viewer is selected;
    // reading viewer then threw an unhandled TypeError. The loader
    // stays on until a viewer is selected and its embed arrives, as before.
    if (!viewer) return;
    const answer = await request(
      `${API_DEFAULT}/${API.embedToken}?workspace-id=${viewer.workspace_id}&report-id=${viewer.report_id}`, {
      headers: {
        "Authorization": "Bearer " + access
      }
    }
    )

    if (tokenExpired(answer) && !afterRefresh) {
      let newAccess = await getAccessTokenWithRefresh();
      if (newAccess) {
        setAccessToken(newAccess);
        localStorage.setItem('accessToken', newAccess);

        // Once: a back that keeps answering token_not_valid would otherwise
        // be asked again with every refresh, without end.
        return fetchPBIEmbedService(newAccess, true);
      }
      setShowLoader(false)
      return
    }
    else if (answer.ok && answer.data && answer.data['accessToken']) {
      setShowLoader(false)
      setPbiEmbed(answer.data)
      onEmbedded()
    }
    else {
      // The report of the previous viewer is taken down rather than left on
      // screen as if it were this one's.
      setShowLoader(false)
      setPbiEmbed({})
      setMessage("No fue posible cargar el visor. Intente de nuevo o elija otro.")
      setSeverity("danger")
      setTimeout(() => { setShowAlert(true) }, 0)
    }
  }

  // The report of the viewer on screen, asked for when that viewer changes.
  useEffect(() => {
    fetchPBIEmbedService() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, viewer]);

  return (
    <div className={`${ViewerStyle["main-container"]}${wide ? ` ${ViewerStyle["show-main-container"]}` : ""}${viewer && viewer.show_page_navigation ? ` ${ViewerStyle["with-page-navigation"]}` : ""}`}>
      {/* <div className={ViewerStyle["main-container"]}> */}
      {
        // The report itself, once there is a token for it.
        pbiEmbed['accessToken'] !== undefined ?
          <PowerBIEmbed
            embedConfig={
              {
                type: 'report', // Supported types: report, dashboard, tile, visual, and qna.
                id: pbiEmbed['reportConfig'][0]['reportId'],
                embedUrl: pbiEmbed['reportConfig'][0]['embedUrl'],
                accessToken: pbiEmbed['accessToken'],
                tokenType: models.TokenType.Embed, // Use models.TokenType.Aad if you're embedding for your organization.
                settings: {
                  panes: {
                    filters: {
                      expanded: false,
                      visible: false
                    },
                    pageNavigation: {
                      visible: Boolean(viewer.show_page_navigation)
                    }

                  },
                }
              }
            }
            eventHandlers={
              new Map([
                ['loaded', function () {
                  console.log('Report loaded');
                }],
                ['rendered', function () {
                  console.log('Report rendered');
                }],
                ['error', function (event) {
                  console.log(event.detail);
                }]
              ])
            }
            cssClassName={
              ViewerStyle["report-style-class"]
            }
            getEmbeddedComponent={
              (embeddedReport) => {
                window.report = embeddedReport;
              }
            }
          />
          :
          null

      }

    </div>
  );
}

export default ViewerEmbed;
